import { createStore } from "zustand/vanilla";

/**
 * Window manager propio (decisión C1 tras el spike F0-T05: react-rnd exigía 4+
 * workarounds contra os-spec §1). Lógica pura: el viewport entra por parámetro,
 * el store jamás toca `window` — por eso los tests corren en node.
 *
 * El componente <WindowFrame> traduce pointer events a estas acciones y pinta
 * con translate3d; este store es la única fuente de verdad de geometría y foco.
 */

export const WM = {
  MIN_W: 280,
  MIN_H: 180,
  TITLEBAR_H: 32,
  TASKBAR_H: 44,
  CASCADE: 28,
  /** A cuántos px del borde el drag dispara snap a mitades */
  EDGE_SNAP_PX: 12,
} as const;

export interface Viewport {
  vw: number;
  vh: number;
}

export interface WindowGeom {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type SnapSide = "left" | "right";

export interface WindowState extends WindowGeom {
  id: string;
  z: number;
  minimized: boolean;
  maximized: boolean;
  snapped: SnapSide | null;
  /** Geometría previa al snap, para restaurar al des-snapear */
  unsnapped: WindowGeom | null;
  /** Geometría previa al maximizar, para restaurar */
  unmaximized: WindowGeom | null;
}

/** Forma serializable para el save (layout persiste por caso, os §1) */
export interface WindowsLayout {
  windows: Record<string, WindowState>;
  nextZ: number;
}

export interface WindowStoreState {
  windows: Record<string, WindowState>;
  nextZ: number;

  open: (id: string, vp: Viewport, geom?: Partial<WindowGeom>) => void;
  close: (id: string) => void;
  focus: (id: string) => void;
  toggleMinimize: (id: string) => void;
  move: (id: string, x: number, y: number, vp: Viewport) => void;
  resizeTo: (id: string, geom: WindowGeom, vp: Viewport) => void;
  snap: (id: string, side: SnapSide, vp: Viewport) => void;
  unsnap: (id: string, vp: Viewport) => void;
  /** Maximiza al área útil o restaura la geometría previa (doble clic / botón ▢) */
  toggleMaximize: (id: string, vp: Viewport) => void;
  /** Tab: enfoca la no-minimizada más al fondo → rotación completa */
  cycleFocus: () => void;
  getLayout: () => WindowsLayout;
  setLayout: (layout: WindowsLayout) => void;
}

/** Área útil (el taskbar no es territorio de ventanas) */
function usable(vp: Viewport): { w: number; h: number } {
  return { w: vp.vw, h: vp.vh - WM.TASKBAR_H };
}

/** Clamp de geometría al área útil — lo usa también <WindowFrame> durante el drag */
export function clampWindowGeom(geom: WindowGeom, vp: Viewport): WindowGeom {
  const area = usable(vp);
  const w = Math.min(Math.max(geom.w, WM.MIN_W), area.w);
  const h = Math.min(Math.max(geom.h, WM.MIN_H), area.h);
  const x = Math.min(Math.max(geom.x, 0), area.w - w);
  const y = Math.min(Math.max(geom.y, 0), area.h - h);
  return { x, y, w, h };
}

export function selectFocusedId(s: Pick<WindowStoreState, "windows">): string | null {
  let top: WindowState | null = null;
  for (const w of Object.values(s.windows)) {
    if (w.minimized) continue;
    if (!top || w.z > top.z) top = w;
  }
  return top?.id ?? null;
}

export function createWindowStore() {
  return createStore<WindowStoreState>()((set, get) => {
    const update = (id: string, patch: Partial<WindowState>) =>
      set((s) => {
        const win = s.windows[id];
        if (!win) return s;
        return { windows: { ...s.windows, [id]: { ...win, ...patch } } };
      });

    return {
      windows: {},
      nextZ: 1,

      open: (id, vp, geom) =>
        set((s) => {
          const existing = s.windows[id];
          if (existing) {
            return {
              windows: {
                ...s.windows,
                [id]: { ...existing, minimized: false, z: s.nextZ },
              },
              nextZ: s.nextZ + 1,
            };
          }
          const n = Object.keys(s.windows).length;
          const base: WindowGeom = {
            x: geom?.x ?? 64 + n * WM.CASCADE,
            y: geom?.y ?? 48 + n * WM.CASCADE,
            w: geom?.w ?? 560,
            h: geom?.h ?? 400,
          };
          const win: WindowState = {
            id,
            ...clampWindowGeom(base, vp),
            z: s.nextZ,
            minimized: false,
            maximized: false,
            snapped: null,
            unsnapped: null,
            unmaximized: null,
          };
          return { windows: { ...s.windows, [id]: win }, nextZ: s.nextZ + 1 };
        }),

      close: (id) =>
        set((s) => {
          const { [id]: _closed, ...rest } = s.windows;
          return { windows: rest };
        }),

      focus: (id) =>
        set((s) => {
          const win = s.windows[id];
          if (!win || win.minimized) return s;
          if (selectFocusedId(s) === id) return s; // ya está al tope
          return {
            windows: { ...s.windows, [id]: { ...win, z: s.nextZ } },
            nextZ: s.nextZ + 1,
          };
        }),

      toggleMinimize: (id) => {
        const win = get().windows[id];
        if (!win) return;
        if (win.minimized) {
          // restaurar = también enfocar
          set((s) => ({
            windows: { ...s.windows, [id]: { ...win, minimized: false, z: s.nextZ } },
            nextZ: s.nextZ + 1,
          }));
        } else {
          update(id, { minimized: true });
        }
      },

      move: (id, x, y, vp) => {
        const win = get().windows[id];
        if (!win || win.maximized) return;
        const clamped = clampWindowGeom({ ...win, x, y }, vp);
        update(id, { x: clamped.x, y: clamped.y, snapped: null });
      },

      resizeTo: (id, geom, vp) => {
        const win = get().windows[id];
        if (!win || win.maximized) return;
        update(id, { ...clampWindowGeom(geom, vp), snapped: null, unsnapped: null });
      },

      toggleMaximize: (id, vp) => {
        const win = get().windows[id];
        if (!win) return;
        if (win.maximized) {
          const prev = win.unmaximized ?? { x: 64, y: 48, w: 560, h: 400 };
          update(id, { ...clampWindowGeom(prev, vp), maximized: false, unmaximized: null });
        } else {
          const area = usable(vp);
          update(id, {
            unmaximized: { x: win.x, y: win.y, w: win.w, h: win.h },
            maximized: true,
            snapped: null,
            x: 0,
            y: 0,
            w: area.w,
            h: area.h,
          });
        }
      },

      snap: (id, side, vp) => {
        const win = get().windows[id];
        if (!win) return;
        const area = usable(vp);
        const half = Math.floor(area.w / 2);
        update(id, {
          // recuerda la geometría pre-snap solo si no venía ya snapeada
          unsnapped: win.snapped ? win.unsnapped : { x: win.x, y: win.y, w: win.w, h: win.h },
          snapped: side,
          x: side === "left" ? 0 : area.w - half,
          y: 0,
          w: half,
          h: area.h,
        });
      },

      unsnap: (id, vp) => {
        const win = get().windows[id];
        if (!win?.snapped || !win.unsnapped) return;
        update(id, { ...clampWindowGeom(win.unsnapped, vp), snapped: null, unsnapped: null });
      },

      cycleFocus: () => {
        const s = get();
        let bottom: WindowState | null = null;
        for (const w of Object.values(s.windows)) {
          if (w.minimized) continue;
          if (!bottom || w.z < bottom.z) bottom = w;
        }
        if (bottom) s.focus(bottom.id);
      },

      getLayout: () => {
        const { windows, nextZ } = get();
        return structuredClone({ windows, nextZ });
      },

      setLayout: (layout) => set(structuredClone(layout)),
    };
  });
}

export type WindowStore = ReturnType<typeof createWindowStore>;
